/**
 * Utility for adding timeout to async operations.
 * Consolidates duplicated timeout logic from across the codebase.
 */

type WithTimeoutOptions<T> = {
  promise: Promise<T>;
  timeoutMs: number;
  fallback?: T;
};

/**
 * Wraps a promise with a timeout. If the promise doesn't resolve within
 * the specified time, either returns a fallback value or rejects with an error.
 *
 * @param options.promise - The promise to wrap
 * @param options.timeoutMs - Timeout in milliseconds
 * @param options.fallback - Optional fallback value to return on timeout (instead of rejecting)
 * @returns The result of the promise or the fallback value
 * @throws Error if timeout occurs and no fallback is provided
 */
const withTimeout = <T>({
  promise,
  timeoutMs,
  fallback,
}: WithTimeoutOptions<T>): Promise<T> => {
  const timeoutPromise = new Promise<T>((resolve, reject) => {
    setTimeout(() => {
      if (fallback !== undefined) {
        resolve(fallback);
      } else {
        reject(new Error(`Operation timed out after ${timeoutMs}ms`));
      }
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]);
};

export default withTimeout;
